<template>
  <div class="notification-dropdown">
    <!-- Notification Bell Button -->
    <button 
      class="pc-head-link notification-bell" 
      @click="toggleDropdown"
      :class="{ 'has-notifications': unreadCount > 0 }"
    >
      <i class="ti ti-bell"></i>
      <span v-if="unreadCount > 0" class="notification-badge">{{ displayCount }}</span>
    </button>

    <!-- Dropdown Menu -->
    <div 
      class="notification-dropdown-menu" 
      :class="{ 'show': isDropdownOpen }"
      @click.stop
    >
      <!-- Header -->
      <div class="dropdown-header">
        <h6 class="mb-0">{{ $t('notifications.title') }}</h6>
        <div class="d-flex gap-2">
          <button 
            v-if="unreadCount > 0"
            class="btn btn-sm btn-outline-primary"
            @click="markAllAsRead"
            :disabled="markingAllRead"
          >
            <span v-if="markingAllRead" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('notifications.markAllRead') }}
          </button>
          <button 
            class="btn btn-sm btn-outline-secondary"
            @click="refreshNotifications"
            :disabled="loading"
          >
            <i class="ti ti-refresh" :class="{ 'spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading && notifications.length === 0" class="dropdown-loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0" class="dropdown-empty">
        <i class="ti ti-bell-off"></i>
        <p>{{ $t('notifications.empty') }}</p>
      </div>

      <!-- Notifications List -->
      <div v-else class="notifications-list">
        <div 
          v-for="notification in notifications" 
          :key="notification.id"
          class="notification-item"
          :class="{ 'unread': !notification.isRead }"
          @click="handleNotificationClick(notification)"
        >
          <div class="notification-icon">
            <i :class="getNotificationIcon(notification.type)"></i>
          </div>
          <div class="notification-content">
            <h6 class="notification-title">{{ notification.title }}</h6>
            <p class="notification-message">{{ notification.message }}</p>
            <small class="notification-time">{{ formatDateTime(notification.createdAt) }}</small>
          </div>
          <div v-if="!notification.isRead" class="unread-indicator"></div>
        </div>
      </div>

      <!-- Footer -->
      <div class="dropdown-footer">
        <router-link 
          to="/notifications" 
          class="btn btn-sm btn-primary w-100"
          @click="closeDropdown"
        >
          {{ $t('notifications.viewAll') }}
        </router-link>
      </div>
    </div>

    <!-- Backdrop -->
    <div 
      v-if="isDropdownOpen" 
      class="notification-backdrop"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useDateFormatter } from '@/composables/useDateFormatter';
import userNotificationService from '@/services/userNotificationService';

export default {
  name: 'NotificationDropdown',
  setup() {
    const router = useRouter();
    const store = useStore();
    const { formatDateTime } = useDateFormatter();

    const isDropdownOpen = ref(false);
    const notifications = ref([]);
    const loading = ref(false);
    const markingAllRead = ref(false);

    const currentUser = computed(() => store.getters['auth/currentUser']);

    const unreadCount = computed(() => {
      return notifications.value.filter(n => !n.isRead).length;
    });

    const displayCount = computed(() => {
      return unreadCount.value > 99 ? '99+' : unreadCount.value.toString();
    });

    const getNotificationIcon = (type) => {
      const iconMap = {
        comment: 'ti ti-message-circle text-primary',
        quotation: 'ti ti-file-text text-info',
        system: 'ti ti-settings text-warning',
        default: 'ti ti-bell text-secondary'
      };
      return iconMap[type] || iconMap.default;
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
      if (isDropdownOpen.value && notifications.value.length === 0) {
        loadNotifications();
      }
    };

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const loadNotifications = async () => {
      if (!currentUser.value?.id) return;
      
      loading.value = true;
      try {
        const response = await userNotificationService.getRecentNotifications(5);
        if (response && Array.isArray(response)) {
          notifications.value = response.map(notification => 
            userNotificationService.transformNotification(notification)
          );
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
        notifications.value = [];
      } finally {
        loading.value = false;
      }
    };

    const refreshNotifications = () => {
      loadNotifications();
    };

    const markAllAsRead = async () => {
      if (!currentUser.value?.id) return;
      
      markingAllRead.value = true;
      try {
        await userNotificationService.markAllAsRead(currentUser.value.id);
        notifications.value.forEach(n => n.isRead = true);
      } catch (error) {
        console.error('Error marking all as read:', error);
      } finally {
        markingAllRead.value = false;
      }
    };

    const handleNotificationClick = async (notification) => {
      // Always mark as read when clicked
      if (!notification.isRead) {
        try {
          await userNotificationService.markAsRead([notification.id]);
          notification.isRead = true;
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      }
      // Handle navigation based on notification data
      const target = userNotificationService.resolveNavigationTarget(notification)
      if (target?.path) {
        router.push({
          path: target.path,
          query: target.query || {}
        })
        closeDropdown()
      }
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.notification-dropdown')) {
        closeDropdown();
      }
    };

    // Listen for Firebase notifications to refresh the list
    const handleFirebaseNotification = () => {
      loadNotifications();
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('firebase-notification-received', handleFirebaseNotification);
      loadNotifications();
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('firebase-notification-received', handleFirebaseNotification);
    });

    return {
      isDropdownOpen,
      notifications,
      loading,
      markingAllRead,
      unreadCount,
      displayCount,
      toggleDropdown,
      closeDropdown,
      refreshNotifications,
      markAllAsRead,
      handleNotificationClick,
      getNotificationIcon,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.notification-dropdown {
  position: relative;
}

.notification-bell {
  position: relative;
  color: var(--bs-body-color);
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: none;
  transition: all 0.3s ease;
}

.notification-bell:hover {
  background: rgba(var(--bs-primary-rgb), 0.1);
  color: var(--bs-primary);
}

.notification-bell.has-notifications {
  color: var(--bs-primary);
}

.notification-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: var(--bs-danger);
  color: white;
  border-radius: 50%;
  border: 2px solid white;
  font-size: 9px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  transform: scale(1);
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0% {
    transform: scale(1);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.5);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }
}

.notification-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  width: 380px;
  max-height: 500px;
  background: white;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
}

.notification-dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dropdown-header h6 {
  color: var(--bs-body-color);
  font-weight: 600;
}

.dropdown-loading, .dropdown-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--bs-secondary-color);
}

.dropdown-empty i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
  opacity: 0.5;
}

.notifications-list {
  max-height: 320px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background: var(--bs-gray-50);
}

.notification-item.unread {
  background: rgba(var(--bs-primary-rgb), 0.02);
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-gray-100);
  border-radius: 50%;
  margin-right: 12px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--bs-body-color);
  line-height: 1.3;
}

.notification-message {
  font-size: 13px;
  color: var(--bs-secondary-color);
  margin-bottom: 6px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: 11px;
  color: var(--bs-secondary-color);
  opacity: 0.7;
}

.unread-indicator {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: var(--bs-primary);
  border-radius: 50%;
}

.dropdown-footer {
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
}

.notification-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .notification-dropdown-menu {
    width: 320px;
    right: -20px;
  }
}
</style>
